import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Save, Type, Phone, Mail, Instagram, Plus, X, ChevronUp, ChevronDown } from 'lucide-react';

const SiteSettingsManager = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/admin/site-settings');
      setSettings(data);
    } catch {
      toast.error('Erreur de chargement');
    }
  };

  useEffect(() => { load(); }, []);

  const set = (k, v) => setSettings((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/admin/site-settings', settings);
      setSettings((p) => ({ ...p, socials: data.socials ?? p.socials }));
      toast.success('Paramètres enregistrés');
    } catch (e) { toast.error(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Erreur'); }
    finally { setSaving(false); }
  };

  if (!settings) return <p className="text-center text-[#6B6B6B] py-20">Chargement…</p>;

  return (
    <div data-testid="admin-sitesettings-manager">
      <div className="flex justify-between items-end mb-10 flex-wrap gap-4">
        <div>
          <span className="text-xs uppercase tracking-[0.4em] text-[#111111] mb-2 block">Identité & contact</span>
          <h2 className="text-4xl font-serif text-[#111111]">Paramètres du site</h2>
          <p className="text-sm text-[#6B6B6B] mt-3 max-w-2xl">
            Slogan d'accueil, coordonnées et réseaux sociaux affichés dans le footer.
          </p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="sitesettings-save"
          className="inline-flex items-center gap-2 px-6 py-3 text-xs uppercase tracking-[0.3em] bg-[#111111] text-white hover:bg-[#333333] transition-colors disabled:opacity-50"
        >
          <Save className="w-4 h-4" /> {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>

      <Group title="Slogan d'accueil" icon={Type}>
        <Row>
          <Input label="Surtitre (non affiché depuis la Phase 1)" value={settings.hero_kicker || ''} onChange={(v) => set('hero_kicker', v)} placeholder="Maison Événementielle — Europe & International" testId="sitesettings-hero-kicker" />
        </Row>
        <Row>
          <Input label="Slogan — première ligne" value={settings.hero_slogan_top || ''} onChange={(v) => set('hero_slogan_top', v)} placeholder="L'art du détail," testId="sitesettings-slogan-top" />
          <Input label="Slogan — deuxième ligne" value={settings.hero_slogan_bottom || ''} onChange={(v) => set('hero_slogan_bottom', v)} placeholder="la signature de l'élégance." testId="sitesettings-slogan-bottom" />
        </Row>
        <Row>
          <Input label="Phrase d'ambiance (non affichée depuis la Phase 1)" value={settings.hero_tagline || ''} onChange={(v) => set('hero_tagline', v)} placeholder="Une orchestration minutieuse pour des instants d'éternité." testId="sitesettings-hero-tagline" full />
        </Row>
      </Group>

      <Group title="Coordonnées (footer)" icon={Mail}>
        <Row>
          <Input label="Email" type="email" value={settings.footer_email || ''} onChange={(v) => set('footer_email', v)} placeholder="hello@jbmhevents.com" testId="sitesettings-footer-email" icon={Mail} />
          <Input label="Téléphone" type="tel" value={settings.footer_phone || ''} onChange={(v) => set('footer_phone', v)} placeholder="+32 ..." testId="sitesettings-footer-phone" icon={Phone} />
        </Row>
        <Row>
          <Input label="Phrase signature (footer)" value={settings.footer_tagline || ''} onChange={(v) => set('footer_tagline', v)} placeholder="L'art du détail, la signature de l'élégance" testId="sitesettings-footer-tagline" full />
        </Row>
      </Group>

      <Group title="Réseaux sociaux (footer et page Contact)" icon={Instagram}>
        <SocialsEditor list={settings.socials || []} onChange={(v) => set('socials', v)} />
      </Group>
    </div>
  );
};

const Group = ({ title, icon: Icon, children }) => (
  <div className="mb-10 bg-white border border-[#111111]/10 p-8">
    <div className="flex items-center gap-3 mb-6 pb-4 border-b border-[#111111]/10">
      {Icon && <Icon className="w-4 h-4 text-[#111111]" />}
      <h3 className="text-sm uppercase tracking-[0.3em] text-[#111111]">{title}</h3>
    </div>
    {children}
  </div>
);

const Row = ({ children }) => <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-5 last:mb-0">{children}</div>;

const Input = ({ label, value, onChange, placeholder, testId, type = 'text', icon: Icon, full }) => (
  <label className={`block ${full ? 'md:col-span-2' : ''}`}>
    <span className="text-[10px] uppercase tracking-[0.3em] text-[#6B6B6B] mb-2 block">{label}</span>
    <div className="relative">
      {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6B6B]/50" />}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className={`w-full border border-[#111111]/20 bg-white py-3 ${Icon ? 'pl-10 pr-4' : 'px-4'} text-sm font-serif focus:border-[#111111] focus:outline-none transition-colors`}
      />
    </div>
  </label>
);

const NETWORKS = [{ v: 'instagram', l: 'Instagram', ph: 'https://instagram.com/jbmhevents' }, { v: 'tiktok', l: 'TikTok', ph: 'https://tiktok.com/@jbmhevents' }];
const fieldCls = 'border border-[#111111]/20 bg-white py-2.5 px-3 text-sm focus:border-[#111111] focus:outline-none transition-colors';

const SocialsEditor = ({ list, onChange }) => {
  const upd = (i, k, v) => onChange(list.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const move = (i, d) => { const n = [...list]; [n[i], n[i + d]] = [n[i + d], n[i]]; onChange(n); };
  return (
    <div data-testid="sitesettings-socials">
      {list.length === 0 && <p className="text-xs text-[#6B6B6B] italic mb-4">Aucun lien pour l'instant.</p>}
      <div className="space-y-3">
        {list.map((l, i) => (
          <div key={i} className="grid grid-cols-1 md:grid-cols-[140px_1fr_2fr_auto] gap-2 items-center" data-testid={`social-row-${i}`}>
            <select value={l.network} onChange={(e) => upd(i, 'network', e.target.value)} className={fieldCls} data-testid={`social-network-${i}`}>
              {NETWORKS.map((n) => <option key={n.v} value={n.v}>{n.l}</option>)}
            </select>
            <input value={l.label} onChange={(e) => upd(i, 'label', e.target.value)} placeholder="Nom affiché (ex. JBMH Héritage)" maxLength={80} className={fieldCls} data-testid={`social-label-${i}`} />
            <input value={l.url} onChange={(e) => upd(i, 'url', e.target.value)} placeholder={NETWORKS.find((n) => n.v === l.network)?.ph} className={fieldCls} data-testid={`social-url-${i}`} />
            <div className="flex">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="w-8 h-8 flex items-center justify-center text-[#6B6B6B] hover:text-[#111111] disabled:opacity-20" aria-label="Monter"><ChevronUp className="w-4 h-4" /></button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === list.length - 1} className="w-8 h-8 flex items-center justify-center text-[#6B6B6B] hover:text-[#111111] disabled:opacity-20" aria-label="Descendre"><ChevronDown className="w-4 h-4" /></button>
              <button type="button" onClick={() => onChange(list.filter((_, j) => j !== i))} className="w-8 h-8 flex items-center justify-center text-[#6B6B6B] hover:text-red-600" aria-label="Retirer" data-testid={`social-remove-${i}`}><X className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mt-5">
        {NETWORKS.map((n) => (
          <button key={n.v} type="button" onClick={() => onChange([...list, { network: n.v, label: '', url: '' }])} data-testid={`social-add-${n.v}`}
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#111111]/20 text-[11px] uppercase tracking-[0.2em] hover:border-[#111111] transition-colors">
            <Plus className="w-3.5 h-3.5" /> Ajouter un lien {n.l}
          </button>
        ))}
      </div>
    </div>
  );
};

export default SiteSettingsManager;
